import { collection, deleteDoc, doc, getDocs, orderBy, query, serverTimestamp, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../../core/firebase/firebase';
import { ERPUser } from '../../../core/auth/types';
import { ClientRecord, FamilyMember } from '../types/clientTypes';

const clientsCol = collection(db, 'clients');

function cleanPhone(value: string) {
  return String(value || '').replace(/[^0-9+]/g, '');
}

function safeDocId(phoneFull: string) {
  const cleaned = cleanPhone(phoneFull);
  return cleaned || `CLIENT-${Date.now()}`;
}

function clientCodeFromPhone(phoneFull: string) {
  const cleaned = cleanPhone(phoneFull);
  return cleaned ? `CL-${cleaned.slice(-8)}` : `CL-${Date.now()}`;
}

export function emptyClient(): Partial<ClientRecord> {
  return {
    firstName: '',
    lastName: '',
    fullName: '',
    phoneCountryCode: '+1',
    phoneNumber: '',
    phoneFull: '',
    otherPhone: '',
    email: '',
    usCity: '',
    indiaCity: '',
    label: 'general',
    familyMembers: [],
    address: '',
    birthDate: '',
    anniversary: '',
    passportExpiry: '',
    visaExpiry: '',
    passportData: { pptNo: '', nationality: '', dob: '', expiryDate: '', gender: '' },
    ccData: { no: '', nameOnCard: '', expiry: '', code: '', type: '', address: '' },
    note: '',
    photoUrl: '',
    status: 'active'
  };
}

function normalizeFamily(raw: any): FamilyMember[] {
  return Array.isArray(raw) ? raw.map(x => ({
    name: String(x?.name || ''),
    relation: String(x?.relation || ''),
    phone: String(x?.phone || '')
  })) : [];
}

function splitName(fullName: string) {
  const parts = fullName.trim().split(/\s+/);
  return { firstName: parts[0] || '', lastName: parts.slice(1).join(' ') || '' };
}

export function normalizeClient(id: string, raw: Record<string, any>): ClientRecord {
  const phoneCountryCode = String(raw.phoneCountryCode || '+1');
  const phoneNumber = String(raw.phoneNumber || raw.phone || '');
  const phoneFull = String(raw.phoneFull || `${phoneCountryCode}${phoneNumber}`.trim());
  const rawFull = String(raw.fullName || raw.name || `${raw.firstName || ''} ${raw.lastName || ''}`.trim() || 'Unnamed Client');
  const split = splitName(rawFull);

  return {
    id,
    clientCode: String(raw.clientCode || clientCodeFromPhone(phoneFull)),
    firstName: String(raw.firstName || split.firstName),
    lastName: String(raw.lastName || split.lastName),
    fullName: rawFull,
    phoneCountryCode,
    phoneNumber,
    phoneFull,
    otherPhone: String(raw.otherPhone || ''),
    email: String(raw.email || ''),
    usCity: String(raw.usCity || ''),
    indiaCity: String(raw.indiaCity || raw.indianCity || ''),
    label: String(raw.label || 'general'),
    familyMembers: normalizeFamily(raw.familyMembers),
    address: String(raw.address || ''),
    birthDate: String(raw.birthDate || ''),
    anniversary: String(raw.anniversary || ''),
    passportExpiry: String(raw.passportExpiry || raw.passportData?.expiryDate || ''),
    visaExpiry: String(raw.visaExpiry || ''),
    passportData: {
      pptNo: String(raw.passportData?.pptNo || ''),
      nationality: String(raw.passportData?.nationality || ''),
      dob: String(raw.passportData?.dob || ''),
      expiryDate: String(raw.passportData?.expiryDate || raw.passportExpiry || ''),
      gender: (raw.passportData?.gender || '') as 'M' | 'F' | ''
    },
    ccData: {
      no: String(raw.ccData?.no || ''),
      nameOnCard: String(raw.ccData?.nameOnCard || ''),
      expiry: String(raw.ccData?.expiry || ''),
      code: String(raw.ccData?.code || ''),
      type: String(raw.ccData?.type || ''),
      address: String(raw.ccData?.address || '')
    },
    note: String(raw.note || raw.notes || ''),
    photoUrl: String(raw.photoUrl || ''),
    status: (raw.status || 'active') as ClientRecord['status'],
    createdAt: raw.createdAt,
    updatedAt: raw.updatedAt,
    createdBy: String(raw.createdBy || ''),
    updatedBy: String(raw.updatedBy || '')
  };
}

export async function listClients() {
  const snap = await getDocs(query(clientsCol, orderBy('updatedAt', 'desc')));
  return snap.docs.map(d => normalizeClient(d.id, d.data()));
}

export async function findClientDuplicates(form: Partial<ClientRecord>) {
  const all = await listClients();
  const phone = cleanPhone(`${form.phoneCountryCode || ''}${form.phoneNumber || ''}`);
  return all.filter(c => phone && cleanPhone(c.phoneFull) === phone);
}

export async function saveClient(form: Partial<ClientRecord>, user: ERPUser) {
  const phoneFull = `${form.phoneCountryCode || '+1'}${form.phoneNumber || ''}`;
  const id = form.id || safeDocId(phoneFull);
  const firstName = String(form.firstName || '').trim();
  const lastName = String(form.lastName || '').trim();
  const fullName = String(form.fullName || `${firstName} ${lastName}`.trim()).trim();

  const payload = {
    clientCode: form.clientCode || clientCodeFromPhone(phoneFull),
    firstName,
    lastName,
    fullName,
    phoneCountryCode: form.phoneCountryCode || '+1',
    phoneNumber: form.phoneNumber || '',
    phoneFull,
    otherPhone: form.otherPhone || '',
    email: form.email || '',
    usCity: form.usCity || '',
    indiaCity: form.indiaCity || '',
    label: form.label || 'general',
    familyMembers: Array.isArray(form.familyMembers) ? form.familyMembers : [],
    address: form.address || '',
    birthDate: form.birthDate || '',
    anniversary: form.anniversary || '',
    passportExpiry: form.passportExpiry || form.passportData?.expiryDate || '',
    visaExpiry: form.visaExpiry || '',
    passportData: form.passportData || { pptNo: '', nationality: '', dob: '', expiryDate: '', gender: '' },
    ccData: form.ccData || { no: '', nameOnCard: '', expiry: '', code: '', type: '', address: '' },
    note: form.note || '',
    photoUrl: form.photoUrl || '',
    status: form.status || 'active',
    updatedAt: serverTimestamp(),
    updatedBy: user.uid,
    createdBy: form.createdBy || user.uid
  };

  if (!form.id) await setDoc(doc(db, 'clients', id), { ...payload, createdAt: serverTimestamp() }, { merge: true });
  else await updateDoc(doc(db, 'clients', id), payload);
  return id;
}

export async function deleteClient(id: string) {
  return deleteDoc(doc(db, 'clients', id));
}
